import { useState, useEffect, useRef } from 'react'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { createCustomerWithToken, createMailerliteMember, createSubscription, createPaymentIntent, createSupabaseSubscription, createSupabaseUser, verifyCoupon } from '@/services/utils'
import classNames from 'classnames'

// Step 1
import CreateSubscription from '@/components/forms/CreateSubscription'

// Step 2
import CreateAccountWithEmail from '@/components/forms/CreateAccountWithEmail'

// Step 3
import CreatePayment from '@/components/forms/CreatePayment'

const SignupStep = (props: any) => {
  const { title, step, loaded, children } = props

  return (
    <div className={classNames('flex flex-col w-full opacity-0 transition-opacity duration-300 delay-200', {
      'hidden': step !== props.step,
      'opacity-100': loaded
    })}>
      <header className="my-2.5 relative">
        <h5 className="text-base text-center">{title}</h5>

        <span className="uppercase absolute top-[50%] translate-y-[-50%] right-0 text-xs text-grey tracking-wide">Step {step} / 4</span>
      </header>

      {children}
    </div>
  )
}

export default function CreateAccount() {
  const [subscriptionItems, setSubscriptionItems] = useState([])
  const [step, setStep] = useState(1)

  // Step 1
  const [membership, setMembership] = useState<string | null>(null)
  const [membershipLoaded, setMembershipLoaded] = useState(false)

  // Step 2
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [isUnique, setIsUnique] = useState(false)
  const [showEmailError, setShowEmailError] = useState(false)
  const [password, setPassword] = useState('')
  const [detailsLoaded, setDetailsLoaded] = useState(false)

  // Step 3
  const [token, setToken] = useState<string | null>(null)
  const [paymentLoaded, setPaymentLoaded] = useState(false)
  const [cbInstance, setCbInstance] = useState<any>(null)
  const [coupon, setCoupon] = useState<string | null>(null)
  const [paymentIntent, setPaymentIntent] = useState<any>(null)
  const [paypalHandler, setPaypalHandler] = useState<any>(null)
  const cardRef = useRef<any>(null)

  // Step 4
  const [loadingMessage, setLoadingMessage] = useState('')
  const [completedLoaded, setCompletedLoaded] = useState(false)
  const [emailLoaded, setEmailLoaded] = useState(false)

  useEffect(() => {
    const fetchSubscriptionItems = async () => {
      const url = '/api/chargebee/items/prices'

      const response = await fetch(url)

      const { list } = await response.json()
      const orderedList = list.sort((a: any, b: any) => a.item_price.price - b.item_price.price)
      
      setSubscriptionItems(orderedList)

      setMembershipLoaded(true)
    }

    fetchSubscriptionItems()
  }, [])

  useEffect(() => {
    if (!(window as any).Chargebee) {
      return
    } else {
      if (!cbInstance) {
        setCbInstance(
          (window as any).Chargebee.init({
            site: process.env.NEXT_PUBLIC_CHARGEBEE_SITE,
            publishableKey: process.env.NEXT_PUBLIC_CHARGEBEE_PUBLISHABLE_KEY,
          })
        )
      }
    }
  }, [])

  useEffect(() => {
    if (showEmailError) {
      setTimeout(() => {
        setShowEmailError(false)
      }, 4000)
    }
  }, [showEmailError])

  const handleCreateSubscription = async (values: any) => {
    setMembership(values.membership)

    setStep(2)

    setTimeout(() => {
      setDetailsLoaded(true)
    }, 100)

    if (subscriptionItems && subscriptionItems.length > 0) {
      const item: any = subscriptionItems.find((item: any) => item.item_price.id === values.membership)

      if (!item) {
        return
      }

      const price = item.item_price.price

      const data = await createPaymentIntent(price, 'AUD')
      setPaymentIntent(data.paymentIntent)

      /*
       * WIP of Paypal integration
      const paypalHandler = await cbInstance.load('paypal').then((paypal: any) => { return paypal })

      await paypalHandler.setPaymentIntent(data.payment_intent)

      console.log(paypalHandler)
      await paypalHandler.mountPaymentButton('#paypal-container', {
        locale: 'en_AU',
        style: {
          shape: 'pill',
          color: 'gold',
          layout: 'horizontal',
          label: 'paypal',
        },
      })
      */
    }
  }

  const handleCreateAccountWithEmail = (values: any) => {
    setEmail(values.email)
    setFirstName(values.firstName)
    setLastName(values.lastName)
    setPassword(values.password)

    setStep(3)

    setTimeout(() => {
      setPaymentLoaded(true)
    }, 600)
  }

  const handleCreatePayment = async (values: any) => {
    setToken(values.token)
    setLoadingMessage('Creating account...')

    setStep(4)

    setTimeout(() => {
      setCompletedLoaded(true)
    }, 100)

    const { customer, card } = await createCustomerWithToken(firstName, lastName, email, values.token)

    setTimeout(() => {
      setLoadingMessage('Creating subscription...')
    }, 500)

    const subscription = await createSubscription(customer.id, (membership as string), card.payment_source_id, values.coupon ? values.coupon.toUpperCase() : null)

    const supabaseUser = await createSupabaseUser(email, password, firstName, lastName, subscription.customer.id)

    await createSupabaseSubscription(supabaseUser.user.id, subscription.subscription.id, subscription.subscription.status)

    await createMailerliteMember(email, firstName, lastName, values.newsletter)

    // Send email notification for coupon subscription
    // TODO: Test and launch this once Postmark account approved
    // if (values.coupon) {
    //   const url = '/api/notifications/coupon'

    //   const couponOpts = {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify({ email: email, name: `${firstName} ${lastName}`, coupon: values.coupon }),
    //   }

    //   const couponResponse = await fetch(url, couponOpts)

    //   await couponResponse.json()
    // }

    setTimeout(() => {
      setLoadingMessage('Account created.')

      setTimeout(() => {
        setEmailLoaded(true)
      }, 1000)
    }, 1000)
  }

  return (
    <div className="w-[66%] max-w-lg h-full flex flex-col relative mobile:w-full mobile:max-w-none mobile:pb-24 tablet:max-w-none tablet:w-full">
      <section className="w-full flex justify-center items-center grow h-full">
      {step === 1 && (
        <SignupStep title="Select a Plan" step={step} loaded={membershipLoaded}>
          <CreateSubscription items={subscriptionItems} userAction={handleCreateSubscription} />
        </SignupStep>
      )}
      {step === 2 && (
        <SignupStep title="Personal Details" step={step} loaded={detailsLoaded}>
          <CreateAccountWithEmail
            userAction={{ submit: handleCreateAccountWithEmail, unique: setIsUnique, emailError: setShowEmailError }}
          />
        </SignupStep>
      )}
      {step === 3 && (
        <SignupStep title="Payment Details" step={step} loaded={paymentLoaded}>
          <CreatePayment cardRef={cardRef} userAction={{ payment: handleCreatePayment, coupon: verifyCoupon }} />
        </SignupStep>
      )}
      {step === 4 && (
        <SignupStep title="" step={step} loaded={completedLoaded}>
          <div className="my-20 flex flex-col justify-center items-center">
            {loadingMessage && !emailLoaded && (
              <>
                <p className="mb-10 text-slate text-base">{loadingMessage}</p>
                <div role="status">
                  <svg aria-hidden="true" className="w-8 h-8 mr-2 text-grey animate-spin fill-slate" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              </>
            )}
            {emailLoaded && (
              <>
                <p className="mb-10 text-slate text-base">We have sent you an confirmation email to verify your account.</p>
                <EnvelopeIcon className="w-8 h-8 text-slate stroke-1" />
              </>
            )}
          </div>
        </SignupStep>
      )}
      </section>
      <div className="mt-10 w-full hidden">
        <ul className="flex w-full justify-center">
          {[1, 2, 3, 4].map((item) => (
            <li 
              key={item}
              className={
                classNames({
                  'h-2 w-2 rounded-full bg-slate opacity-50 m-1': true,
                  '!opacity-100': step === item,
                })
              }
            >
              <span className="text-gray-400 sr-only">Step {item}</span>
            </li>
          ))}
        </ul>
      </div>
      {!isUnique && showEmailError && (
        <div className="absolute bottom-2.5 w-full flex justify-center items-center">
          <div className="w-full shadow-md rounded border border-grey px-10 pt-4 pb-3.5 bg-cream text-base text-center">
            <p>There is an exsiting account with this email address</p>
          </div>
        </div>
      )}
    </div>
  )
}
